import { useTheme } from "@emotion/react";
import {
  faChartPie,
  faFileContract,
  faFileLines,
  faLaptop,
  faSearch,
  faSquarePlus,
  faSync,
  faUser,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons";
import { CloudSyncOutlined } from "@mui/icons-material";
import axios from "axios";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getEmpresaIdLogada } from "../utils/getEmpresaLogada";
import { obterStorage, salvarStorage } from "../utils/secureStorage";
import useIsDev from "../utils/useIsDev";
import { baseURL, useApi } from "./api";
import { useAuth } from "./auth";

export const MenuContext = createContext({});

export const MenuProvider = ({ children, ignorarPermissoes: ignorarPermissoesProp }) => {
  const [sincronizadorAtivo, setSincronizadorAtivo] = useState(false);
  const sessao = obterStorage("sessao");
  const theme = useTheme();
  const { get, token } = useApi();
  const { homologacao, contratoWaychefAtivo } = useAuth();
  const { isDev } = useIsDev();
  const navigate = useNavigate();

  const buscarSincronizador = async () => {
    try {
      const response = await get("/aplicacoes-servicos/listar");

      response?.data.map(async (servico) => {
        if (servico.tipoServico === "SINCRONIZADOR") {
          const resSincronizador = await get(
            "/aplicacoes-servicos/servico-sincronizador/client-secret/" + servico.clientSecret,
          );

          if (resSincronizador.status === 200 && resSincronizador?.data) {
            setSincronizadorAtivo(resSincronizador.data.ativo);
          }
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const dadosSessaoUsuario = obterStorage("_da");

  const tipoUsuario =
    dadosSessaoUsuario?.usuarioLogadoDTO?.usuarioPerfilEmpresaDTO?.tipoPerfilUsuario;

  const ignorarPermissoesCalculado = ignorarPermissoesProp || tipoUsuario === "MASTER";

  const listaPrivilegios =
    dadosSessaoUsuario?.usuarioLogadoDTO?.usuarioPerfilEmpresaDTO?.listaPrivilegios ?? {};

  const permitirVisualizarMenuCompras =
    tipoUsuario === "MASTER" ||
    typeof listaPrivilegios?.PED_COMPRAS_SUMARIZADO === "string" ||
    typeof listaPrivilegios?.PED_COMPRAS_DETALHADO === "string";

  const permitirVisualizarMenuVendas =
    tipoUsuario === "MASTER" || typeof listaPrivilegios?.PED_VENDAS === "string";

  const permitirVisualizarMenuConfiguracoes =
    tipoUsuario === "MASTER" || typeof listaPrivilegios?.PED_CONFIGURACOES === "string";

  /*const permitirVisualizarZerarEstoque =
      tipoUsuario === "MASTER" ||
      typeof listaPrivilegios?.EST_ZERAR === "string";*/

  const filhosCompras = [
    {
      nome: "Finanças",
      filhos: [
        {
          nome: "Fornecedores e Credores",
          filhos: [
            {
              permissoes: ["PESSOA_CAD_FORN"],
              nome: "Cadastros",
              onClick: () => navigate("/financas/fornecedores-e-credores"),
            },
          ],
        },
        {
          nome: "Representantes",
          permissoes: ["PESSOA_CAD_REPR"],
          filhos: [
            {
              nome: "Cadastros",
              permissoes: ["PESSOA_CAD_REPR"],
              onClick: () => navigate("/financas/representantes"),
            },
          ],
        },
        {
          nome: "Parâmetros",
          filhos: [
            {
              nome: "Categoria de sangria",
              permissoes: ["CX_CAD_CATEGORIA_SANGRIA"],
              onClick: () => navigate("/financas/parametros/categoria-sangria"),
            },
            {
              nome: "Centro de Custos",
              permissoes: ["FIN_CAD_CENTRO_CUSTO"],
              onClick: () => navigate("/financas/parametros/centro-de-custos"),
            },
            {
              nome: "Cadastro de bandeiras",
              permissoes: ["FIN_CAD_BANDEIRAS"],
              onClick: () => navigate("/financas/parametros/bandeira-cartao"),
            },
            {
              nome: "Plano de contas",
              permissoes: ["FIN_CAD_PLANO_CONTAS"],
              onClick: () => navigate("/financas/parametros/plano-de-contas"),
            },
            {
              nome: "Contas Correntes",
              permissoes: ["FIN_CAD_CC"],
              onClick: () => navigate("/financas/parametros/contas-correntes"),
            },
            {
              nome: "CFOP",
              permissoes: ["FISCAL_CAD_CFOP"],
              onClick: () => navigate("/financas/parametros/cfop"),
            },
          ],
        },
        {
          nome: "Movimentações",
          filhos: [
            {
              nome: "Contas a Receber",
              permissoes: ["FIN_CAD_TIT_RECEBER", "FIN_RECEB_BAIXA_TITULO_RECEBER"],
              ignorarPermissoes: ignorarPermissoesCalculado,
              onClick: () => navigate("/financas/movimentacoes/contas-a-receber"),
            },
          ],
        },
      ],
    },
  ];

  if (permitirVisualizarMenuCompras) {
    filhosCompras[0].filhos.push({
      nome: "Compras",
      permissoes: [],
      itemContratoWaychef: ["PEDIDO_COMPRA"],
      filhos: [
        {
          nome: "Pedido de compras",
          onClick: () => navigate("/financas/compras/pedido-compras"),
          permissoes: ["PED_COMPRAS_DETALHADO", "PED_COMPRAS_SUMARIZADO"],
          validarApenasUmaPermissao: true,
        },
        {
          nome: "Recebimento de mercadorias",
          onClick: () => navigate("/financas/compras/mercadoria/recebimento"),
          permissoes: ["PED_COMPRAS_DETALHADO"],
        },
      ],
    });
  }

  if (permitirVisualizarMenuVendas) {
    filhosCompras[0].filhos.push({
      nome: "Vendas",
      permissoes: ["PED_VENDAS"],
      itemContratoWaychef: ["PEDIDO_VENDA"],
      filhos: [
        {
          nome: "Pedido de vendas",
          onClick: () => navigate("/financas/vendas/pedido-vendas"),
          permissoes: ["PED_VENDAS"],
        },
      ],
    });
  }

  if (permitirVisualizarMenuConfiguracoes) {
    filhosCompras[0].filhos.push({
      nome: "Configurações",
      onClick: () => navigate("/financas/compras/configuracoes"),
      permissoes: ["PED_CONFIGURACOES"],
    });
  }

  const homologacaoFormatada = homologacao ? true : baseURL.includes("192") ? true : false;

  const [listaDashboards, setListaDashboards] = useState([]);
  const [moduloDelivery, setModuloDelivery] = useState(undefined);
  const [mostrarGerenciadorFiscal, setMostrarGerenciadorFiscal] = useState(false);
  const [modeloFiscalGerenciador, setModeloFiscalGerenciador] = useState("NFE");
  const [dadosGerenciadorFiscal, setDadosGerenciadorFiscal] = useState(null);

  const onAutorizadaGerenciadorFiscalRef = useRef(null);

  const abrirGerenciadorFiscal = (modelo, dadosIniciais, onAutorizada) => {
    setModeloFiscalGerenciador(modelo || "NFE");
    setDadosGerenciadorFiscal(dadosIniciais || null);
    onAutorizadaGerenciadorFiscalRef.current = onAutorizada || null;
    setMostrarGerenciadorFiscal(true);
  };

  const fecharGerenciadorFiscal = () => {
    setMostrarGerenciadorFiscal(false);
    setDadosGerenciadorFiscal(null);
    onAutorizadaGerenciadorFiscalRef.current = null;
  };

  const notificarAutorizacaoFiscal = (dados) => {
    onAutorizadaGerenciadorFiscalRef.current?.(dados);
  };

  const dadosDeFormaUtilizavel = useMemo(() => {
    const dashboardsFilhos = [...listaDashboards];
    dashboardsFilhos.push({
      nome: "Novo",
      onClick: () => navigate("/dashboard/novo"),
      permissoes: ["CONS_DASHBOARD"],
    });

    const menuConstruido = {
      menus: [
        {
          nome: "Dashboard",
          icone: faChartPie,
          permissoes: ["CONS_DASHBOARD"],
          filhos: dashboardsFilhos,
          tipoEmpresa: ["PLATAFORMA", "INTEGRADOR", "FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
        },
        {
          nome: "Plataforma",
          icone: faFileLines,
          tipoEmpresa: ["PLATAFORMA"],
          filhos: [
            {
              nome: "Bancos",
              onClick: () => navigate("/plataforma/bancos"),
              permissoes: ["PLAT_CAD_BANCO"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Versionamento",
              onClick: () => navigate("/plataforma/versionamento"),
              permissoes: ["PLAT_CAD_VERSIONAMENTO"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Cotação de Moedas",
              onClick: () => navigate("/plataforma/cotacao-moeda"),
            },
            {
              nome: "Formas de Pgto iFood",
              onClick: () => navigate("/plataforma/formas-pagamento-ifood"),
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "ChatBot",
              onClick: () => navigate("/plataforma/chatbot"),
              tipoEmpresa: ["PLATAFORMA"],
            },
            {
              nome: "Certificado Pix Cielo",
              onClick: () => navigate("/plataforma/cielo/certificado-mtls"),
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
          ],
        },
        {
          nome: "Cadastros",
          icone: faSquarePlus,
          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
          filhos: [
            {
              nome: "Produtos",
              filhos: [
                {
                  nome: "Cadastro",
                  permissoes: ["PROD_CAD"],
                  onClick: () => navigate("/produtos/cadastro/listagem"),
                },
                {
                  nome: "Estoque",
                  filhos: [
                    {
                      tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
                      nome: "Locais de Estocagem",
                      permissoes: ["EST_CAD_LOCAL_ESTOCAGEM"],
                      onClick: () => navigate("/produtos/estoques/local-de-estocagem"),
                    },
                    {
                      nome: "Setores",
                      permissoes: ["EST_CAD_SETOR"],
                      onClick: () => navigate("/produtos/estoques/setores"),
                    },
                    {
                      nome: "Ajuste Simples",
                      permissoes: ["EST_AJUSTE_SIMPLES"],
                      onClick: () => navigate("produtos/estoques/simples"),
                    },
                    {
                      nome: "Inventário",
                      permissoes: ["EST_CAD_INVENTARIO"],
                      onClick: () => navigate("produtos/estoques/inventario"),
                    },
                    {
                      nome: "Zerar Estoque",
                      onClick: () => navigate("produtos/estoques/zerar"),
                      permissoes: ["EST_ZERAR"],
                    },
                  ],
                },
                {
                  nome: "Gerador de Etiquetas",
                  itemContratoWaychef: ["CONTROLE_GERADOR_ETIQUETA"],
                  onClick: () => navigate("/produtos/parametros/gerador-etiqueta"),
                  permissoes: ["PROD_GERAR_ETIQUETA"],
                },
                {
                  nome: "Parâmetros",
                  tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
                  filhos: [
                    {
                      nome: "Grupos",
                      onClick: () => navigate("/produtos/parametros/grupos"),
                      permissoes: ["PROD_CAD_GRUPO"],
                      tipoEmpresa: ["FRANQUIA", "MATRIZ", "FILIAL"],
                    },
                    {
                      nome: "Menus",
                      onClick: () => navigate("/produtos/parametros/menus/listagem"),
                      permissoes: ["PROD_CAD_MENU"],
                      tipoEmpresa: ["FRANQUIA", "MATRIZ", "FILIAL"],
                    },
                    {
                      nome: "Observações",
                      onClick: () => navigate("/produtos/parametros/observacoes"),
                      permissoes: ["PROD_CAD_OBS"],
                      tipoEmpresa: ["FRANQUIA", "MATRIZ", "FILIAL"],
                    },
                    {
                      tipoEmpresa: ["FRANQUIA", "MATRIZ", "FILIAL"],
                      nome: "Variações",
                      onClick: () => navigate("/produtos/parametros/variacoes"),
                      permissoes: ["PROD_CAD_VARIACAO"],
                    },
                    {
                      tipoEmpresa: ["FRANQUEADO", "MATRIZ", "FILIAL"],
                      nome: "Locais de Produção",
                      onClick: () => navigate("/produtos/parametros/locais-de-producao"),
                      permissoes: ["PROD_CAD_LOCAL_PRODUCAO"],
                    },
                    {
                      nome: "Fiscais",
                      ocultar: !contratoWaychefAtivo,
                      permissoes: [],
                      filhos: [
                        {
                          nome: "Classificação Fiscal Contribuinte ICMS",
                          onClick: () =>
                            navigate("produtos/parametros/classificacao-fiscal-contribuinte"),
                          permissoes: ["PROD_CAD_CLASS_FISCAL_CONTRIBUINTE"],
                        },
                        {
                          nome: "ISS",
                          onClick: () => navigate("/produtos/parametros/fiscais/iss"),
                          permissoes: ["PROD_CAD_DEP_FISCAL_ISSQN"],
                        },
                        {
                          nome: "PIS/COFINS",
                          onClick: () => navigate("/produtos/parametros/fiscais/pis-cofins"),
                          permissoes: ["PROD_CAD_DEP_FISCAL_PISCOFINS"],
                          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
                        },
                        {
                          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
                          nome: "Tributação por UF de destino",
                          onClick: () =>
                            navigate("/produtos/parametros/fiscais/tributacao-por-uf-de-destino"),
                          permissoes: ["PROD_CAD_TRIB_UF"],
                        },
                        {
                          nome: "Regra Fiscal IBS-CBS",
                          onClick: () => navigate("/produtos/parametros/regras-fiscais"),
                          permissoes: ["PROD_CAD_DEP_FISCAL_ICMS"],
                        },
                        {
                          nome: "ICMS",
                          onClick: () => navigate("/produtos/parametros/fiscais/icms"),
                          permissoes: ["PROD_CAD_DEP_FISCAL_ICMS"],
                        },
                      ],
                    },
                    {
                      tipoEmpresa: ["FRANQUIA", "MATRIZ", "FILIAL"],
                      nome: "Tabelas de preços",
                      onClick: () => navigate("/produtos/parametros/tabela-de-precos"),
                      permissoes: ["PROD_CAD_TBL_PRECO"],
                    },
                  ],
                },
              ],
            },
            {
              nome: "Clientes",
              permissoes: [],
              filhos: [
                {
                  nome: "Cadastro",
                  onClick: () => navigate("/clientes/cadastro/listagem"),
                  permissoes: ["PESSOA_CAD_CLI"],
                },
                {
                  nome: "Configurações PDV",
                  ocultar: !contratoWaychefAtivo,
                  onClick: () => navigate("/clientes/configuracoes"),
                  permissoes: ["PESSOA_CAD_CLI"],
                },
                {
                  nome: "Parâmetros",
                  ocultar: !contratoWaychefAtivo,
                  filhos: [
                    {
                      nome: "Classificações",
                      onClick: () => navigate("/clientes/parametros/classificacoes"),
                      permissoes: ["PESSOA_CAD_CLASSIFICACAO"],
                    },
                    {
                      nome: "Ramos de Atividade",
                      onClick: () => navigate("/clientes/parametros/ramos-de-atividade"),
                      permissoes: ["PESSOA_CAD_RAMO_ATIVIDADE"],
                    },
                    {
                      nome: "Status de Crédito",
                      onClick: () => navigate("/clientes/parametros/status-credito"),
                      permissoes: ["PESSOA_CAD_STATUS_CREDITO"],
                    },
                  ],
                },
              ],
            },
            {
              nome: "Funcionários",
              permissoes: [],
              ocultar: !contratoWaychefAtivo,
              filhos: [
                {
                  nome: "Cadastro",
                  onClick: () => navigate("/dados-empresa/funcionarios"),
                  permissoes: ["PESSOA_CAD_FUNC"],
                },
              ],
            },
            {
              nome: "Departamento Fiscal",
              ocultar: sessao?.empresaDTO?.pais !== "PT",
              onClick: () => navigate("/fiscal/departamento-fiscal"),
            },
            {
              nome: "Marketing",
              permissoes: [],
              filhos: [
                {
                  nome: "Promoters & Parceiros",
                  palavrasChave: ["Parceiros", "Cadastro", "Promoter"],
                  permissoes: ["PESSOA_CAD_PARCEIROS"],
                  onClick: () => navigate("/parceiros/listagem"),
                },
                {
                  nome: "Cupons & Vouchers",
                  palavrasChave: ["Cupons", "Cupom", "Cadastro", "Voucher", "Vouchers"],
                  permissoes: ["WAYMENU_CAD_CUPOM_DESC"],
                  itemContratoWaymenu: ["PEDIDOS"],
                  onClick: () => navigate("/cupons/"),
                },
                {
                  nome: "CRM & Fidelização",
                  indexarNaBusca: true,
                  palavrasChave: ["Fidelidade", "Cashback", "CRM"],
                  itemContratoWaybe: ["CONTROLE_CASHBACK"],
                  itemContratoWaychef: ["CONTROLE_CASHBACK"],
                  filhos: [
                    {
                      nome: "Cadastros",
                      onClick: () => navigate("/fidelidade/cashback"),
                      permissoes: ["PARAM_CAD_CASHBACK"],
                    },
                    {
                      nome: "Configurações",
                      onClick: () => navigate("/fidelidade/cashback/configuracoes"),
                      permissoes: ["PARAM_CAD_CASHBACK"],
                    },
                  ],
                },
                {
                  nome: "Promoções",
                  onClick: () => navigate("/produtos/promocoes"),
                  permissoes: ["PROD_CAD_PROMOCOES"],
                },
                {
                  nome: "Gestão PUB",
                  indexarNaBusca: true,
                  palavrasChave: ["Gestão PUB", "Módulo PUB", "PUB"],
                  itemContratoWaychef: ["MODULO_VENDA_PUB"],
                  filhos: [
                    {
                      nome: "Criar Evento",
                      permissoes: ["CONF_CAD_PUB_EVENTO"],
                      onClick: () => navigate("/modulos/moduloPub/listagemEventos"),
                    },
                    {
                      nome: "Perfil de Cartão",
                      permissoes: ["CONF_CAD_PUB_PERFIL"],
                      onClick: () => navigate("/modulos/moduloPub/gestaoPerfis"),
                    },
                    {
                      nome: "Bloqueio de Cartões",
                      permissoes: ["CONF_PUB"],
                      onClick: () => navigate("/modulos/moduloPub/bloqueioCartoes"),
                    },
                  ],
                },
              ],
            },
            {
              nome: "Módulos",
              permissoes: [],
              filhos: [
                {
                  nome: "Módulos",
                  onClick: () => navigate("/parametros/modulos/listagem"),
                  permissoes: ["CONF_CAD_MODULO"],
                  itemContratoWaymenu: ["CARDAPIO_DIGITAL"],
                },
              ],
            },
            {
              nome: "Clube de Vantagens",
              filhos: [
                {
                  nome: "Categorias",
                  palavrasChave: ["Vantagens", "Categoria de Cupom de Vantagem"],
                  permissoes: ["WAYMENU_CAD_VANTAGEM"],
                  itemContratoWaymenu: ["CUPOM_VANTAGEM"],
                  onClick: () => navigate("/vantagens/categorias"),
                },
                {
                  nome: "Cupons de Vantagens",
                  palavrasChave: ["Vantagens", "Cupom de Vantagem", "Cupons"],
                  permissoes: ["WAYMENU_CAD_VANTAGEM"],
                  itemContratoWaymenu: ["CUPOM_VANTAGEM"],
                  onClick: () => navigate("/vantagens/cadastro"),
                },
              ],
            }
          ],
        },
        {
          nome: "Canais Digitais ",
          palavrasChave: ["Canais Digitais", "Canais"],
          icone: faUtensils,
          filhos: [
            {
              nome: "Wayservice",
              icone: faUtensils,
              tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
              filhos: [
                {
                  nome: "Gestão do Cardápio",
                  palavrasChave: ["Cardápio", "Autoatendimento", "Gestão"],
                  onClick: () => navigate("/produtos/cardapio/wayservice/listagem"),
                  itemContratoWaychef: ["WAYSERVICE"],
                  permissoes: ["PROD_CAD_CARDAPIOS"],
                },
              ],
            },
            {
              nome: "Waymenu",
              itemContratoWaymenu: ["CARDAPIO_DIGITAL"],
              filhos: [
                {
                  nome: "Gestão do Cardápio",
                  palavrasChave: ["Cardápio", "Cadastro"],
                  permissoes: ["WAYMENU_CAD_CARDAPIO"],
                  itemContratoWaymenu: ["CARDAPIO_DIGITAL"],
                  onClick: () => navigate("/cardapio/listagem"),
                },
                {
                  nome: "Cardápio Digital",
                  palavrasChave: ["Cardápio"],
                  onClick: () => window.open("/waymenu/redirect/cardapio"),
                },
                {
                  nome: "Cardápio Delivery",
                  palavrasChave: ["Cardápio", "Delivery"],
                  itemContratoWaymenu: ["PEDIDOS"],
                  oculto: moduloDelivery !== undefined ? !moduloDelivery?.ativo : false,
                  onClick: () => window.open("/waymenu/redirect/delivery"),
                },
                {
                  nome: "Configurar Entrega",
                  palavrasChave: ["Entregas", "Entrega"],
                  permissoes: ["WAYMENU_CAD_ENTREGA_AGENDADA"],
                  itemContratoWaymenu: ["PEDIDOS"],
                  onClick: () => navigate("/entregas"),
                },
                {
                  nome: "Banners",
                  itemContratoWaymenu: ["CARDAPIO_DIGITAL"],
                  permissoes: ["WAYMENU_CAD_BANNER"],
                  onClick: () => navigate("/banners/cadastro"),
                },
                {
                  nome: "Agendamento de Mesas",
                  itemContratoWaymenu: ["RESERVAS_MESAS"],
                  filhos: [
                    {
                      nome: "Cadastro",
                      onClick: () => navigate("/agendamento-mesas/listagem"),
                      permissoes: ["WAYMENU_CAD_RESERVA"],
                    },
                    {
                      nome: "Reservas",
                      onClick: () => navigate("/agendamento-mesas/reservas"),
                      permissoes: ["WAYMENU_RESERVAS"],
                    },
                    {
                      nome: "Reservas de Hoje",
                      onClick: () => navigate("/waymenu/reservas-dia"),
                      permissoes: ["WAYMENU_RESERVAS_DIA"],
                    },
                  ],
                },
              ],
            },
            {
              nome: "Painel de Pedidos",
              palavrasChave: ["Pedidos", "Painel"],
              permissoes: ["WAYMENU_PEDIDOS_ACOMPANHAMENTO"],
              itemContratoWaymenu: ["CARDAPIO_DIGITAL", "PEDIDOS"],
              onClick: () => navigate("/pedidos"),
            },
          ],
        },
        {
          nome: "Consultas Rápidas",
          ocultar: !contratoWaychefAtivo,
          icone: faSearch,
          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
          filhos: [
            {
              nome: "Atendimentos",
              permissoes: ["CONS_ATENDIMENTOS"],
              onClick: () => navigate("/consultas-rapidas/atendimentos"),
            },
            {
              nome: "Caixas",
              permissoes: ["CONS_CAIXA"],
              onClick: () => navigate("/consultas-rapidas/caixas"),
            },
          ],
        },
        {
          nome: "Fiscal",
          icone: faFileLines,
          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
          filhos: [
            {
              nome: "Consultar NF‑e/NFS‑e",
              onClick: () => navigate("/fiscal/nota-fiscal/consultar"),
              permissoes: ["FISCAL_NF_CONSULTAR"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Gerenciador Fiscal",
              onClick: () => abrirGerenciadorFiscal("NFE"),
              permissoes: ["FISCAL_NF_GERENCIAR", "FISCAL_NFCE_GERENCIAR"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Emitir NF‑e/NFS‑e",
              filhos: [
                {
                  nome: "Emitir Nota Fiscal de Saída",
                  onClick: () => navigate("/fiscal/nfe/saida"),
                  permissoes: ["FISCAL_NF_EMISSAO"],
                  nivelNecessario: "CONSULTAR_PERMITIR",
                },
                {
                  nome: "Emitir Nota Fiscal de Entrada",
                  onClick: () => navigate("/fiscal/nfe/entrada"),
                  permissoes: ["FISCAL_NF_EMISSAO"],
                  nivelNecessario: "CONSULTAR_PERMITIR",
                  ocultar: true,
                },
                {
                  nome: "Emitir Nota Fiscal Complementar",
                  onClick: () => navigate("/fiscal/nfe/complementar"),
                  permissoes: ["FISCAL_NF_EMISSAO"],
                  nivelNecessario: "CONSULTAR_PERMITIR",
                  ocultar: true,
                },
              ],
            },
            {
              nome: "Lançar NF‑e",
              ocultar: true,
              filhos: [
                {
                  nome: "Nota Fiscal de Entrada",
                  onClick: () => navigate("/fiscal/nota-fiscal/lancamento/entrada"),
                  permissoes: ["FISCAL_NF_LANCAMENTO"],
                  nivelNecessario: "CONSULTAR_PERMITIR",
                },
              ],
            },
            {
              nome: "Planilha de Preços",
              onClick: () => navigate("/fiscal/planilha-precos"),
              permissoes: ["FISCAL_PLANILHA_PRECO"],
              nivelNecessario: "CONSULTAR_PERMITIR",
              ocultar: true,
            },
          ],
        },
        {
          nome: "Operacional",
          ocultar: !contratoWaychefAtivo,
          icone: faUser,
          filhos: filhosCompras,
          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
        },
        {
          nome: "Relatórios",
          icone: faFileLines,
          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
          filhos: [
            {
              nome: "Cashback",
              itemContratoWaybe: ["CONTROLE_CASHBACK"],
              permissoes: ["PARAM_REL_CASHBACK"],
              onClick: () => navigate("/relatorios/cashback"),
            },
            {
              nome: "Cadastrais",
              filhos: [
                {
                  nome: "Clientes",
                  permissoes: ["REL_CAD_CLIENTE"],
                  onClick: () => navigate("analise-relatorios/cadastrais/clientes"),
                },
                {
                  nome: "Ficha técnica",
                  ocultar: !contratoWaychefAtivo,
                  permissoes: ["REL_CAD_FICHA_TECNICA"],
                  onClick: () => navigate("/analise-relatorios/cadastrais/ficha-tecnica"),
                },
                {
                  nome: "Produtos",
                  onClick: () => navigate("analise-relatorios/cadastrais/produtos"),
                  permissoes: ["REL_CAD_PRODUTO"],
                },
                {
                  nome: "Tributação",
                  ocultar: !contratoWaychefAtivo,
                  permissoes: ["REL_CAD_TRIBUTACAO"],
                  filhos: [
                    {
                      nome: "ICMS",
                      onClick: () => navigate("/analise-relatorios/cadastrais/tributacao/icms"),
                      permissoes: ["REL_CAD_TRIBUTACAO"],
                    },
                    {
                      nome: "PIS/COFINS",
                      onClick: () =>
                        navigate("/analise-relatorios/cadastrais/tributacao/piscofins"),
                      permissoes: ["REL_CAD_TRIBUTACAO"],
                    },
                  ],
                },
              ],
            },
            {
              nome: "Franquia",
              tipoEmpresa: ["FRANQUIA"],
              permissoes: ["REL_FAT_FRANQUIA"],
              filhos: [
                {
                  nome: "Rebate",
                  onClick: () => navigate("/analise-relatorios/faturamento/franquia/rebate"),
                  permissoes: ["REL_FAT_FRANQUIA"],
                },
              ],
            },
            {
              nome: "Fiscais",
              ocultar: !contratoWaychefAtivo,
              permissoes: ["REL_FAT_FISCAIS"],
              filhos: [
                {
                  nome: "Listagem aux. Escrituração fiscal",
                  onClick: () =>
                    navigate(
                      "/analise-relatorios/faturamento/fiscais/listagem-aux-escrituracao-fiscal",
                    ),
                  permissoes: ["REL_FAT_FISCAIS"],
                },
              ],
            },
            {
              nome: "Estoque",
              ocultar: !contratoWaychefAtivo,
              permissoes: ["REL_ESTOQUE"],
              filhos: [
                {
                  nome: "Em falta",
                  onClick: () => navigate("/analise-relatorios/estoque/em-falta"),
                  permissoes: ["REL_ESTOQUE"],
                },
                {
                  nome: "Disponível",
                  onClick: () => navigate("/analise-relatorios/estoque/disponivel"),
                  permissoes: ["REL_ESTOQUE"],
                },
                {
                  nome: "Histórico de movimentações",
                  onClick: () => navigate("/analise-relatorios/estoque/historico-movimentacoes"),
                  permissoes: ["REL_ESTOQUE"],
                },
                {
                  nome: "Geral",
                  onClick: () => navigate("/analise-relatorios/estoque/geral"),
                  permissoes: ["REL_ESTOQUE"],
                },
                {
                  nome: "Movimentações de insumos",
                  onClick: () => navigate("/analise-relatorios/estoque/movimentacoes-insumos"),
                  permissoes: ["REL_ESTOQUE"],
                },
              ],
            },
            {
              nome: "Faturamento",
              ocultar: !contratoWaychefAtivo,
              filhos: [
                {
                  nome: "Curva ABC",
                  onClick: () => navigate("/analise-relatorios/faturamento/curva-abc"),
                  permissoes: ["REL_FAT_PROD_VENDIDOS"],
                },
                {
                  nome: "Cancelamentos",
                  permissoes: ["REL_FAT_CANC"],
                  filhos: [
                    {
                      nome: "Itens cancelados",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/cancelamentos/itens-cancelados"),
                      permissoes: ["REL_FAT_CANC"],
                    },
                  ],
                },
                {
                  nome: "Produtos Vendidos",
                  permissoes: ["REL_FAT_PROD_VENDIDOS"],
                  filhos: [
                    {
                      nome: "Top 10",
                      onClick: () => navigate("/analise-relatorios/produtos-vendidos/top10"),
                      permissoes: ["REL_FAT_PROD_VENDIDOS"],
                    },
                    {
                      nome: "Por tabela de preço",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/produtos-vendidos/tabela-preco"),
                      permissoes: ["REL_FAT_PROD_VENDIDOS"],
                    },
                    {
                      nome: "Por pacote",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/produtos-vendidos/por-pacote"),
                      permissoes: ["REL_FAT_PROD_VENDIDOS"],
                    },
                    {
                      nome: "Por grupo",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/produtos-vendidos/por-grupo"),
                      permissoes: ["REL_FAT_PROD_VENDIDOS"],
                    },
                    {
                      nome: "Itens vendidos",
                      onClick: () =>
                        navigate(
                          "/analise-relatorios/faturamento/produtos-vendidos/itens-vendidos",
                        ),
                      permissoes: ["REL_FAT_PROD_VENDIDOS"],
                    },
                    {
                      nome: "Por vendedor",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/produtos-vendidos/por-vendedor"),
                      permissoes: ["REL_FAT_PROD_VENDIDOS"],
                    },
                  ],
                },
                {
                  nome: "Módulos",
                  permissoes: ["REL_FAT_MODULOS"],
                  filhos: [
                    {
                      nome: "Delivery",
                      onClick: () => navigate("/analise-relatorios/faturamento/modulos/delivery"),
                      permissoes: ["REL_FAT_MODULOS"],
                    },
                    {
                      nome: "Controle de tempo produção",
                      onClick: () =>
                        navigate(
                          "/analise-relatorios/faturamento/modulos/controle-tempo-de-producao",
                        ),
                      permissoes: ["REL_FAT_MODULOS"],
                    },
                  ],
                },
                {
                  nome: "Monetário",
                  permissoes: ["REL_FAT_MONETARIO"],
                  filhos: [
                    {
                      nome: "Anual (mês a mês)",
                      onClick: () => navigate("/analise-relatorios/faturamento/monetario/anual"),
                      permissoes: ["REL_FAT_MONETARIO"],
                    },
                    {
                      nome: "Atendimentos",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/monetario/atendimentos"),
                      permissoes: ["REL_FAT_MONETARIO"],
                    },
                    {
                      nome: "Taxas de serviço",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/monetario/taxas-servico"),
                      permissoes: ["REL_FAT_MONETARIO"],
                    },
                    {
                      nome: "Comissão do vendedor",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/monetario/comissao-vendedor"),
                      permissoes: ["REL_FAT_MONETARIO"],
                    },
                    {
                      nome: "Faturamento por período",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/monetario/por-periodo"),
                      permissoes: ["REL_FAT_MONETARIO"],
                    },
                    {
                      nome: "Por módulo",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/monetario/por-modulo"),
                      permissoes: ["REL_FAT_MONETARIO"],
                    },
                    {
                      nome: "Formas de pagamento",
                      onClick: () =>
                        navigate("/analise-relatorios/faturamento/monetario/formas-de-pagamento"),
                      permissoes: ["REL_FAT_MONETARIO"],
                    },
                  ],
                },
              ],
            },
            {
              nome: "Finanças",
              ocultar: !contratoWaychefAtivo,
              permissoes: ["REL_FINANCAS"],
              filhos: [
                {
                  nome: "Contas a Pagar",
                  permissoes: ["REL_FINANCAS"],
                  onClick: () => navigate("/analise-relatorios/financas/contas-a-pagar"),
                },
                {
                  nome: "Contas a Receber",
                  permissoes: ["REL_FINANCAS"],
                  onClick: () => navigate("/analise-relatorios/financas/contas-a-receber"),
                },
              ],
            },
            {
              nome: "Pedidos",
              ocultar: !contratoWaychefAtivo,
              permissoes: ["REL_PEDIDOS"],
              tipoEmpresa: ["FRANQUIA", "MATRIZ"],
              filhos: [
                {
                  nome: "Pedidos de vendas",
                  onClick: () => navigate("/analise-relatorios/pedidos/pedidos-de-vendas"),
                  permissoes: ["REL_PEDIDOS"],
                },
                {
                  nome: "Apuração de Royalties",
                  onClick: () => navigate("/analise-relatorios/pedidos/apuracao-royalties"),
                  tipoEmpresa: ["FRANQUIA"],
                  permissoes: ["FIN_CAD_ROYALTIES"],
                },
              ],
            },
          ],
        },
        {
          nome: "Contrato",
          icone: faFileContract,
          tipoEmpresa: ["PLATAFORMA", "INTEGRADOR"],
          permissoes: [],
          filhos: [
            {
              nome: "Waychef",
              tipoEmpresa: ["PLATAFORMA", "INTEGRADOR"],
              permissoes: ["PLAT_CONTRATOS"],
              filhos: [
                {
                  nome: "Planos padrão",
                  tipoEmpresa: ["PLATAFORMA"],
                  permissoes: ["PLAT_CONTRATOS"],
                  onClick: () => navigate("/contrato/plataforma/waychef/planos"),
                },
                {
                  nome: "Planos",
                  tipoEmpresa: ["INTEGRADOR"],
                  permissoes: ["PLAT_CONTRATOS"],
                  onClick: () => navigate("/contrato/integrador/waychef/planos"),
                },
              ],
            },
            {
              nome: "Waymenu",
              tipoEmpresa: ["PLATAFORMA", "INTEGRADOR"],
              permissoes: ["PLAT_CONTRATOS"],
              filhos: [
                {
                  nome: "Planos padrão",
                  tipoEmpresa: ["PLATAFORMA"],
                  permissoes: ["PLAT_CONTRATOS"],
                  onClick: () => navigate("/contrato/plataforma/waymenu/planos"),
                },
                {
                  nome: "Planos",
                  tipoEmpresa: ["INTEGRADOR"],
                  permissoes: ["PLAT_CONTRATOS"],
                  onClick: () => navigate("/contrato/integrador/waymenu/planos"),
                },
              ],
            },
            {
              nome: "Extras",
              tipoEmpresa: ["INTEGRADOR"],
              permissoes: ["PLAT_CONTRATOS_EXTRAS"],
              onClick: () => navigate("contrato/integrador/extras"),
            },
            {
              nome: "Extras padrão",
              tipoEmpresa: ["PLATAFORMA"],
              permissoes: ["PLAT_CONTRATOS_EXTRAS"],
              onClick: () => navigate("contrato/plataforma/extras"),
            },
            {
              nome: "Controle de Contratos",
              tipoEmpresa: ["PLATAFORMA", "INTEGRADOR"],
              onClick: () => navigate("/empresas/controle-contratos"),
              permissoes: ["PLAT_CONTRATOS"],
            },
          ],
        },
        {
          nome: "Relatórios",
          icone: faFileContract,
          tipoEmpresa: ["PLATAFORMA", "INTEGRADOR"],
          permissoes: ["PLAT_RELATORIOS"],
          filhos: [
            {
              nome: "Transações",
              tipoEmpresa: ["PLATAFORMA", "INTEGRADOR"],
              onClick: () => navigate("/analise-relatorios/transacoes"),
              permissoes: ["PLAT_TRANSACOES"],
            },
            {
              nome: "Faturamento Integrador",
              tipoEmpresa: ["PLATAFORMA"],
              permissoes: ["PLAT_RELATORIOS"],
              onClick: () => navigate("/analise-relatorios/faturamento-integradores"),
            },
            {
              nome: "Faturamento Bilhetagem",
              tipoEmpresa: ["INTEGRADOR"],
              permissoes: ["PLAT_RELATORIOS"],
              onClick: () => navigate("/analise-relatorios/faturamento-bilhetagem"),
            },
            {
              nome: "Auditoria Itens",
              tipoEmpresa: ["PLATAFORMA", "INTEGRADOR"],
              permissoes: ["PLAT_RELATORIOS"],
              onClick: () => navigate("/analise-relatorios/auditoria-itens"),
            },
            {
              nome: "Auditoria Contratos",
              tipoEmpresa: ["PLATAFORMA"],
              permissoes: ["PLAT_RELATORIOS"],
              onClick: () => navigate("/analise-relatorios/auditoria-contratos"),
            },
            {
              nome: "Chatbot - Satisfação",
              tipoEmpresa: ["PLATAFORMA"],
              onClick: () => navigate("/plataforma/chatbot/relatorio-satisfacao"),
            },
          ],
        },
        {
          nome: "Waymenu",
          tipoEmpresa: ["PLATAFORMA"],
          icone: faUtensils,
          itemContratoWaymenu: ["BILHETAGEM", "CARDAPIO_DIGITAL", "PEDIDOS"],
          filhos: [
            {
              nome: "Cardápio",
              onClick: () => navigate("/cardapio/listagem"),
              permissoes: ["WAYMENU_CAD_CARDAPIO"],
            },
            {
              nome: "Entregas",
              onClick: () => navigate("/entregas"),
              permissoes: ["WAYMENU_CAD_ENTREGAS"],
            },
          ],
        },
        {
          nome: "Serviços",
          icone: faLaptop,
          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
          filhos: [
            {
              nome: "Serviços do Sistema",
              permissoes: ["PARAM_SERVICOS_SISTEMA"],
              onClick: () => navigate("/servicos/servicosdosistema"),
            },
          ],
        },
        sincronizadorAtivo && {
          nome: "Sincronizador",
          icone: faSync,
          itemContratoWaychef: ["SINCRONIZADOR"],
          tipoEmpresa: ["FRANQUIA", "FRANQUEADO", "MATRIZ", "FILIAL"],
          permissoes: [],
          svg: <CloudSyncOutlined htmlColor={theme.cores.textoSimples} />,
          filhos: [
            {
              nome: "Carga Inicial",
              permissoes: ["PARAM_SERVICOS_SISTEMA"],
              onClick: () => navigate("/sincronizador/carga-inicial"),
            },
          ],
        },
        {
          nome: "Fiscal",
          icone: faFileLines,
          tipoEmpresa: ["PLATAFORMA"],
          filhos: [
            {
              nome: "Parâmetros",
              filhos: [
                {
                  nome: "Códigos de Serviço",
                  onClick: () => navigate("/produtos/parametros/fiscais/codigo-servico"),
                },
              ],
            },
            {
              nome: "Código CEST",
              onClick: () => navigate("/plataforma/fiscal/codigo-cest"),
              permissoes: ["PLAT_CAD_FISCAL_CEST"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Código NCM",
              onClick: () => navigate("/plataforma/fiscal/codigo-ncm"),
              permissoes: ["PLAT_CAD_FISCAL_NCM"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Código CST",
              onClick: () => navigate("/plataforma/fiscal/cst"),
              permissoes: ["PLAT_CAD_FISCAL_CST"],
            },
            {
              nome: "CSOSN ICMS",
              onClick: () => navigate("/plataforma/fiscal/cosn-icms"),
              permissoes: ["PLAT_CAD_FISCAL_CSOSN"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "CST ICMS",
              onClick: () => navigate("/plataforma/fiscal/cst-icms"),
              permissoes: ["PLAT_CAD_FISCAL_CST"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "CST PIS/COFINS",
              onClick: () => navigate("/plataforma/fiscal/cst-pis-cofins"),
              permissoes: ["PLAT_CAD_FISCAL_PIS_COFINS"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Informação Complementar Documento Fiscal ICMS",
              onClick: () =>
                navigate("/plataforma/fiscal/informacao-complementar-documento-fiscal-icms"),
              permissoes: ["PLAT_CAD_INF_COMP_DOC_FISCAL"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Origem Mercadoria",
              onClick: () => navigate("/plataforma/fiscal/origem-mercadoria"),
              permissoes: ["PLAT_CAD_FISCAL_ORIGEM_MERCADORIA"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Classificação Tributária CST",
              onClick: () => navigate("/plataforma/fiscal/classificacao-tributaria-cst"),
              permissoes: ["PLAT_CAD_FISCAL_CST"],
            },
            {
              nome: "CFOP Default",
              permissoes: ["PLAT_CAD_FISCAL_CFOP_PADRAO", ignorarPermissoesCalculado],
              ignorarPermissoes: ignorarPermissoesCalculado,
              nivelNecessario: "CONSULTAR_PERMITIR",
              onClick: () => navigate("/plataforma/fiscal/cfop-default"),
            },
            {
              // TODO: usar o código de permissão real assim que for definido e
              // registrado no backend (core/TipoPrivilegioCodigo + PermissaoFactory).
              nome: "Software Fiscal",
              onClick: () => navigate("/plataforma/fiscal/software-fiscal"),
            },
          ],
        },
        {
          nome: "SiTef",
          icone: faFileLines,
          tipoEmpresa: ["PLATAFORMA"],
          filhos: [
            {
              nome: "Identificação Carteiras Digitais",
              onClick: () => navigate("/plataforma/sitef/identificacao-carteiras-digitais"),
              permissoes: ["PLAT_CAD_SITEF_CARTEIRAS_DIGITAIS"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Redes Adquirentes",
              onClick: () => navigate("/plataforma/sitef/redes-adquirentes"),
              permissoes: ["PLAT_CAD_SITEF_REDES_ADQUIRENTES"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
            {
              nome: "Identificação Bandeiras",
              onClick: () => navigate("/plataforma/sitef/identificacao-bandeiras"),
              permissoes: ["PLAT_CAD_SITEF_BANDEIRAS"],
              nivelNecessario: "CONSULTAR_PERMITIR",
            },
          ],
        },
      ].filter(Boolean),
    };

    const ordemMenusPlataforma = ["Dashboard", "Plataforma", "Contrato", "Fiscal", "SiTef", "Relatórios"];
    menuConstruido.menus.sort((primeiroMenu, segundoMenu) => {
      const indicePrimeiroMenu = ordemMenusPlataforma.indexOf(primeiroMenu.nome);
      const indiceSegundoMenu = ordemMenusPlataforma.indexOf(segundoMenu.nome);

      if (indicePrimeiroMenu === -1 || indiceSegundoMenu === -1) {
        return 0;
      }

      return indicePrimeiroMenu - indiceSegundoMenu;
    });

    return menuConstruido;
  }, [
    contratoWaychefAtivo,
    homologacaoFormatada,
    isDev,
    sincronizadorAtivo,
    moduloDelivery,
    listaDashboards,
    theme,
    ignorarPermissoesCalculado,
  ]);

  const [listaMenu, setListaMenuUtilizavel] = useState(dadosDeFormaUtilizavel);
  const [headerFixo, setHeaderFixo] = useState(false);
  const [carregandoDashboard, setCarregandoDashboard] = useState(true);
  const [dadosEmpresa, setDadosEmpresa] = useState({});
  const [dadosUsuario, setDadosUsuario] = useState({});

  function atualizarHeaderFixo(status) {
    try {
      setHeaderFixo(status);
    } catch (e) { }
  }

  useEffect(() => {
    setListaMenuUtilizavel(dadosDeFormaUtilizavel);
  }, [dadosDeFormaUtilizavel]);

  function buscarDashboards(tokenAtual = token, montarMenu = true) {
    return new Promise(async (resolve, reject) => {
      if (!tokenAtual && sessao?.tokenDTO?.token) {
        tokenAtual = sessao.tokenDTO.token;
      }

      if (!tokenAtual) {
        resolve([]);
        return;
      }

      const idEmpresa = getEmpresaIdLogada();
      const api = axios.create({
        baseURL: baseURL,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${tokenAtual}`,
          ...(idEmpresa && { "X-IdEmpresa": idEmpresa }),
        },
      });

      await api
        .get("/relatorios/dashboard")
        .then((dados) => {
          salvarStorage("lista_dashboards", dados.data);

          if (montarMenu) {
            buscarDashboardsStorage();
          }
          resolve(dados);
        })
        .catch((error) => {
          resolve([]);
        });
    });
  }

  async function buscarDashboardsStorage() {
    try {
      const lista = obterStorage("lista_dashboards");
      if (lista == null) {
        buscarDashboards();
        return;
      }

      if (typeof lista === "object" && Array.isArray(lista)) {
        const formatados = lista.map((item) => ({
          nome: item.nome,
          onClick: () => {
            navigate(`/dashboard/${item.urlAmigavel}`);
            navigate(0);
          },
        }));

        setListaDashboards(formatados);
      }
    } catch (e) {
      buscarDashboards();
    }
  }

  const buscarDadosEmpresaStorage = async () => {
    try {
      setDadosEmpresa({});
      setDadosUsuario({});
      const dados = obterStorage("_da");
      setDadosEmpresa(dados?.empresaDTO);
      setDadosUsuario(dados?.usuarioLogadoDTO);
    } catch (error) {
      toast.error("Não foi possivel carregar os dados de sua empresa!");
    }
  };

  function buscarModuloDelivery(byforce = false) {
    if (moduloDelivery?.tipo && !byforce) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      await get("/gestao-consumo/modulo/buscar-todos")
        .then((response) => {
          const moduloDelivery = response.data.content.filter((item) => item.tipo === "DELIVERY");

          if (moduloDelivery.length > 0) {
            setModuloDelivery(moduloDelivery[0]);
          } else {
            setModuloDelivery({
              ativo: false,
              tipo: "DELIVERY",
            });
          }
        })
        .catch((error) => {
          console.error(error);
          setModuloDelivery({
            ativo: false,
            tipo: "DELIVERY",
          });
        });
    });
  }

  useEffect(() => {
    if (token) {
      buscarSincronizador();
      buscarModuloDelivery();

      setTimeout(() => {
        buscarDashboardsStorage();
      }, 500);
    }
  }, [token]);

  return (
    <MenuContext.Provider
      value={{
        headerFixo,
        listaMenu,
        atualizarHeaderFixo,
        buscarDashboards,
        setCarregandoDashboard,
        carregandoDashboard,
        buscarDashboardsStorage,
        buscarModuloDelivery,
        dadosEmpresa,
        buscarDadosEmpresaStorage,
        dadosUsuario,
        listaDashboards,
        mostrarGerenciadorFiscal,
        modeloFiscalGerenciador,
        dadosGerenciadorFiscal,
        abrirGerenciadorFiscal,
        fecharGerenciadorFiscal,
        notificarAutorizacaoFiscal,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => useContext(MenuContext);
